import { cs } from '../../../lib/currency';
import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, Loader2, PlusCircle } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import { toast } from 'sonner';
import api from '../../../services/api';
import SaveCardOnFileDialog from '../SaveCardOnFileDialog';

/**
 * Past-due lane bookings banner used by AdminBookingModal.
 *
 * When the admin picks an existing customer, we fetch
 * `GET /lanes/admin/customers/{id}/past-due-bookings` and render a soft
 * yellow banner showing total + count + an expandable inline collect-
 * payment panel. Once payments are recorded, we re-fetch — if no past-
 * dues remain the banner auto-dismisses (per product decision).
 *
 * Hard block for self-bookers happens server-side in the
 * `create-payment-intent` endpoint (returns HTTP 409 with the same
 * payload shape, frontend can surface a similar list).
 */
const METHODS = [
  { value: 'cash', label: 'Cash', online: false },
  { value: 'check', label: 'Check', online: false },
  { value: 'zelle', label: 'Zelle', online: false },
  { value: 'venmo', label: 'Venmo', online: false },
  { value: 'card', label: 'Card (manual swipe)', online: false },
  { value: 'credit', label: 'Account Credit', online: false },
  { value: 'other', label: 'Other', online: false },
  // Stripe charge — uses the customer's saved card via the existing
  // /admin/invoices/charge endpoint. Surcharge handled server-side.
  { value: 'stripe_charge', label: 'Charge Card on File (Stripe)', online: true },
];

export default function PastDuePanel({ customerId, onClearedAll }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ count: 0, total: 0, bookings: [] });
  const [expanded, setExpanded] = useState(false);
  // Map<booking_id, {selected, amount, method, reference, notes}>
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);
  // Saved payment methods on file for this customer — disables the Stripe
  // option in the dropdown when empty so admin doesn't try to charge a
  // card that doesn't exist (was producing silent 400s).
  const [hasCardOnFile, setHasCardOnFile] = useState(null); // null = unknown / not yet checked
  const [customerInfo, setCustomerInfo] = useState(null);   // {id, name, email} — for the save-card dialog
  const [saveCardOpen, setSaveCardOpen] = useState(false);

  const fetchPastDue = async () => {
    if (!customerId) return;
    try {
      setLoading(true);
      const res = await api.get(`/lanes/admin/customers/${customerId}/past-due-bookings`);
      const d = res.data || { count: 0, total: 0, bookings: [] };
      setData(d);
      // Seed drafts with booking amount + cash default; selected=true.
      const seed = {};
      for (const b of d.bookings) {
        seed[b.id] = {
          selected: true,
          amount: b.amount,
          method: 'cash',
          reference: '',
          notes: '',
        };
      }
      setDrafts(seed);
      if (d.count === 0) {
        setExpanded(false);
        onClearedAll?.();
      }
    } catch (err) {
      // Silent: banner just stays hidden.
      setData({ count: 0, total: 0, bookings: [] });
    } finally {
      setLoading(false);
    }
  };

  // Check whether this customer has a saved card so we can either enable
  // or disable the "Charge Card on File" option in the method dropdown.
  // Soft-fail: if the endpoint errors we just leave the option enabled
  // and surface the underlying error in the toast on submit.
  const checkCardOnFile = async () => {
    if (!customerId) return;
    try {
      const res = await api.get(`/admin/parent/${customerId}/payment-methods`);
      const methods = res.data?.payment_methods || [];
      const hasCard = methods.some((m) => m.type === 'card');
      setHasCardOnFile(hasCard);
    } catch (err) {
      // Failed lookup — leave the option enabled, server will reject if needed.
      setHasCardOnFile(null);
    }
  };

  // Fetch the customer's name/email so the SaveCardOnFileDialog can show
  // them — the past-due endpoint returns just bookings, not the parent.
  const fetchCustomerInfo = async () => {
    if (!customerId) {
      setCustomerInfo(null);
      return;
    }
    try {
      const res = await api.get(`/admin/customers/${customerId}`);
      const u = res.data || {};
      setCustomerInfo({ id: customerId, name: u.name, email: u.email });
    } catch (err) {
      setCustomerInfo({ id: customerId, name: null, email: null });
    }
  };

  useEffect(() => {
    fetchPastDue();
    checkCardOnFile();
    fetchCustomerInfo();
    // re-fetch any time the customer changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const selectedTotal = useMemo(() => {
    let s = 0;
    for (const b of data.bookings) {
      const d = drafts[b.id];
      if (d?.selected) s += Number(d.amount) || 0;
    }
    return Math.round(s * 100) / 100;
  }, [data.bookings, drafts]);

  const anySelected = useMemo(
    () => Object.values(drafts).some((d) => d?.selected),
    [drafts],
  );

  const updateDraft = (id, patch) =>
    setDrafts((m) => ({ ...m, [id]: { ...m[id], ...patch } }));

  const handleSave = async () => {
    const selected = data.bookings.filter((b) => drafts[b.id]?.selected);
    if (selected.length === 0) return;
    setSaving(true);
    try {
      // Split into Stripe-charge bookings (call /admin/invoices/charge per
      // booking — the endpoint handles surcharge, sets payment_status=paid,
      // and stamps stripe_payment_intent_id) vs offline bookings (bulk
      // record-payment for cash/check/zelle/venmo/etc).
      const stripeRows = selected.filter((b) => drafts[b.id]?.method === 'stripe_charge');
      const offlineRows = selected.filter((b) => drafts[b.id]?.method !== 'stripe_charge');

      let stripeOk = 0;
      let stripeFail = 0;
      const stripeErrors = [];
      for (const b of stripeRows) {
        try {
          await api.post('/admin/invoices/charge', {
            invoice_id: b.id,
            invoice_type: 'lane_booking',
          });
          stripeOk += 1;
        } catch (err) {
          stripeFail += 1;
          // Always log so the admin can grab the full payload from the
          // browser console if the toast got dismissed.
          // eslint-disable-next-line no-console
          console.error('[PastDuePanel] Stripe charge failed for booking', b.id, err.response?.data || err);
          const status = err.response?.status;
          const detailRaw = err.response?.data?.detail;
          let humanMsg;
          if (typeof detailRaw === 'string') {
            humanMsg = detailRaw;
          } else if (detailRaw && typeof detailRaw === 'object') {
            humanMsg = detailRaw.message || detailRaw.error || JSON.stringify(detailRaw);
          } else {
            humanMsg = err.message || 'Stripe charge failed';
          }
          // Make the most common case crystal clear.
          if (/saved payment method|no payment method/i.test(humanMsg)) {
            humanMsg = 'No card on file for this customer. Ask them to save a card in Account → Payment Methods, then retry.';
          }
          const dateLabel = b.date || b.start_time || b.id?.slice(0, 8);
          stripeErrors.push(`Booking ${dateLabel}: ${humanMsg}${status ? ` (HTTP ${status})` : ''}`);
        }
      }

      let offlineRecorded = 0;
      let offlineFailed = 0;
      if (offlineRows.length > 0) {
        const payments = offlineRows.map((b) => ({
          booking_id: b.id,
          payment_method: drafts[b.id].method,
          amount: Number(drafts[b.id].amount) || undefined,
          reference_number: drafts[b.id].reference || undefined,
          notes: drafts[b.id].notes || undefined,
        }));
        const res = await api.post('/lanes/admin/bookings/bulk-record-payment', { payments });
        const r = res.data || {};
        offlineRecorded = r.recorded || 0;
        offlineFailed = r.failed || 0;
      }

      const totalRecorded = stripeOk + offlineRecorded;
      const totalFailed = stripeFail + offlineFailed;
      if (totalRecorded > 0) {
        const breakdown = [];
        if (stripeOk > 0) breakdown.push(`${stripeOk} Stripe`);
        if (offlineRecorded > 0) breakdown.push(`${offlineRecorded} offline`);
        toast.success(`Recorded ${totalRecorded} payment${totalRecorded === 1 ? '' : 's'} (${breakdown.join(' + ')})`);
      }
      if (totalFailed > 0) {
        const firstErr = stripeErrors[0] || `${totalFailed} payment${totalFailed === 1 ? '' : 's'} failed`;
        // Sticky-ish toast (8s) so admin has time to read the reason.
        toast.error(firstErr, {
          duration: 8000,
          description:
            stripeErrors.length > 1
              ? `+ ${stripeErrors.length - 1} more error${stripeErrors.length - 1 === 1 ? '' : 's'} — see browser console for full details`
              : undefined,
        });
      }
      await fetchPastDue();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payments');
    } finally {
      setSaving(false);
    }
  };

  if (!customerId || loading || data.count === 0) return null;

  return (
    <div
      className="rounded-md border border-amber-300 bg-amber-50 p-3"
      data-testid="past-due-banner"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-amber-900">
              {data.count} past-due lane booking{data.count === 1 ? '' : 's'} · {cs()}{data.total.toFixed(2)}
            </div>
            <div className="text-xs text-amber-800">
              Record an offline payment (cash/check/Zelle) or charge the customer's saved card via Stripe.
            </div>
          </div>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="border-amber-400 text-amber-900 hover:bg-amber-100 shrink-0"
          onClick={() => setExpanded((v) => !v)}
          data-testid="past-due-toggle-btn"
        >
          {expanded ? (
            <><ChevronUp className="h-3 w-3 mr-1" /> Hide</>
          ) : (
            <><ChevronDown className="h-3 w-3 mr-1" /> Collect / Record</>
          )}
        </Button>
      </div>

      {expanded && (
        <div className="mt-3 space-y-2" data-testid="past-due-collect-panel">
          <div className="max-h-56 overflow-y-auto divide-y divide-amber-200 border border-amber-200 rounded bg-white">
            {data.bookings.map((b) => {
              const d = drafts[b.id] || {};
              return (
                <div key={b.id} className="p-2 text-xs" data-testid={`past-due-row-${b.id}`}>
                  <label className="flex items-center justify-between gap-2 cursor-pointer">
                    <span className="flex items-center gap-2 min-w-0">
                      <input
                        type="checkbox"
                        checked={!!d.selected}
                        onChange={(e) => updateDraft(b.id, { selected: e.target.checked })}
                        data-testid={`past-due-select-${b.id}`}
                      />
                      <span className="truncate">
                        <span className="font-medium">{b.lane_name}</span>
                        {' · '}{b.date}{' '}
                        <span className="text-slate-500">
                          {b.start_time}{b.end_time ? `–${b.end_time}` : ''}
                        </span>
                      </span>
                    </span>
                    <span className="font-medium text-amber-900 shrink-0">
                      {cs()}{Number(b.amount || 0).toFixed(2)}
                    </span>
                  </label>
                  {d.selected && (
                    <div className="mt-2 grid grid-cols-2 gap-2 pl-6">
                      <Select
                        value={d.method || 'cash'}
                        onValueChange={(v) => updateDraft(b.id, { method: v })}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid={`past-due-method-${b.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {METHODS.map((m) => {
                            const stripeDisabled = m.value === 'stripe_charge' && hasCardOnFile === false;
                            return (
                              <SelectItem
                                key={m.value}
                                value={m.value}
                                disabled={stripeDisabled}
                                data-testid={`past-due-method-option-${m.value}`}
                              >
                                {m.label}
                                {stripeDisabled && <span className="text-[10px] text-slate-400 ml-1">(no card on file)</span>}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        step="0.01"
                        className="h-8 text-xs"
                        value={d.amount ?? ''}
                        onChange={(e) => updateDraft(b.id, { amount: e.target.value })}
                        placeholder="Amount"
                        disabled={d.method === 'stripe_charge'}
                        data-testid={`past-due-amount-${b.id}`}
                      />
                      {(d.method === 'check' || d.method === 'zelle' || d.method === 'venmo') && (
                        <Input
                          className="h-8 text-xs col-span-2"
                          value={d.reference || ''}
                          onChange={(e) => updateDraft(b.id, { reference: e.target.value })}
                          placeholder={d.method === 'check' ? 'Check number' : 'Reference #'}
                          data-testid={`past-due-ref-${b.id}`}
                        />
                      )}
                      {d.method === 'stripe_charge' && (
                        <div className="col-span-2 text-[11px] text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1">
                          Stripe will charge the customer's saved card. CC surcharge (if enabled) will be added server-side. Amount = booking total.
                        </div>
                      )}
                      {hasCardOnFile === false && d.method !== 'stripe_charge' && (
                        <div className="col-span-2 flex items-center justify-between gap-2 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
                          <span>No card on file — Stripe option disabled.</span>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-6 px-2 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            onClick={() => setSaveCardOpen(true)}
                            data-testid={`past-due-add-card-${b.id}`}
                          >
                            <PlusCircle className="h-3 w-3 mr-1" />
                            Add card
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between gap-2 pt-1">
            <div className="text-xs text-amber-900">
              <span className="font-semibold">{Object.values(drafts).filter((d) => d?.selected).length}</span> selected ·{' '}
              <span className="font-semibold">{cs()}{selectedTotal.toFixed(2)}</span>
            </div>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving || !anySelected}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="past-due-save-btn"
            >
              {saving ? (
                <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Saving…</>
              ) : (
                'Collect / Record Payments'
              )}
            </Button>
          </div>
        </div>
      )}

      <SaveCardOnFileDialog
        open={saveCardOpen}
        onOpenChange={setSaveCardOpen}
        parent={customerInfo || { id: customerId }}
        onSaved={async () => {
          // Re-probe payment methods so the Stripe option re-enables.
          await checkCardOnFile();
        }}
      />
    </div>
  );
}
